import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  memo,
} from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  MapPin,
  User,
  Lightbulb,
  CheckCircle2,
  ChevronDown,
  Check,
  Search,
  X,
  Phone,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  getChapters,
  getInterests,
  getCountries,
  register as registerApi,
  login as sendOtpApi,
  verifyOtp as verifyOtpApi,
} from "@/Services/AuthServices";

// ---------------------------------------------------------------------------
// PURE HELPERS
// ---------------------------------------------------------------------------

const isoToFlagEmoji = (iso) => {
  if (!iso || iso.length !== 2) return "";
  const codePoints = [...iso.toUpperCase()].map(
    (c) => 127397 + c.charCodeAt(0),
  );
  return String.fromCodePoint(...codePoints);
};

const getDialCode = (c) => {
  if (!c) return "";
  const raw =
    c.code ??
    c.dialCode ??
    c.phoneCode ??
    c.callingCode ??
    c.countryCallingCode ??
    "";
  const s = String(raw).trim();
  if (!s) return "";
  return s.startsWith("+") ? s : `+${s}`;
};

const getIso2 = (c) => {
  if (!c) return "";
  return (c.isoCode ?? c.iso2 ?? c.countryCode ?? c.sortname ?? c.code2 ?? "")
    .toString()
    .trim()
    .toUpperCase();
};

const getFlag = (c) => {
  if (!c) return "";
  return c.flag || c.flagEmoji || c.emoji || isoToFlagEmoji(getIso2(c));
};

// ---------------------------------------------------------------------------
// STATIC INTEREST FALLBACK
// ---------------------------------------------------------------------------

const STATIC_INTERESTS = [
  { _id: "1", name: "Finance" },
  { _id: "2", name: "Marketing" },
  { _id: "3", name: "Trade Finance" },
  { _id: "4", name: "New Markets" },
  { _id: "5", name: "Self Development" },
  { _id: "6", name: "Investment" },
  { _id: "7", name: "Coaching" },
  { _id: "8", name: "Cybersecurity" },
];

// ---------------------------------------------------------------------------
// STEP INDICATOR — now 4 steps
// ---------------------------------------------------------------------------

const StepIndicator = ({ current }) => {
  const steps = [
    { icon: User, label: "Profile" },
    { icon: Phone, label: "Verify" },
    { icon: MapPin, label: "Centre" },
    { icon: Lightbulb, label: "Interests" },
  ];

  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {steps.map((s, i) => {
        const Icon = s.icon;
        const isComplete = i < current;
        const isActive = i === current;

        return (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${
                  isComplete
                    ? "bg-[#C6A87C] border-[#C6A87C]"
                    : isActive
                      ? "border-[#C6A87C] bg-[#C6A87C]/10"
                      : "border-white/20 bg-transparent"
                }`}
              >
                {isComplete ? (
                  <CheckCircle2 size={16} className="text-[#061412]" />
                ) : (
                  <Icon
                    size={15}
                    className={isActive ? "text-[#C6A87C]" : "text-white/30"}
                  />
                )}
              </div>
              <span
                className={`text-[10px] uppercase tracking-wider ${
                  isActive
                    ? "text-[#C6A87C]"
                    : isComplete
                      ? "text-white/60"
                      : "text-white/25"
                }`}
              >
                {s.label}
              </span>
            </div>

            {i < steps.length - 1 && (
              <div
                className={`h-px w-8 mb-5 mx-1 transition-colors ${
                  i < current ? "bg-[#C6A87C]" : "bg-white/10"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// COUNTRY PHONE PICKER
// ---------------------------------------------------------------------------

const CountryPhonePicker = memo(function CountryPhonePicker({
  countries,
  selectedCountry,
  onSelectCountry,
  phone,
  onPhoneChange,
  error,
  disabled,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        getDialCode(c).includes(q) ||
        getIso2(c).toLowerCase().includes(q),
    );
  }, [countries, search]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => searchRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || highlightIndex < 0) return;
    const listEl = listRef.current;
    if (!listEl) return;
    const item = listEl.children[highlightIndex + 1];
    if (item) item.scrollIntoView({ block: "nearest" });
  }, [isOpen, highlightIndex]);

  const toggleOpen = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => {
      if (!prev) {
        const idx = countries.findIndex(
          (c) => getIso2(c) === getIso2(selectedCountry),
        );
        setHighlightIndex(idx >= 0 ? idx : 0);
        setSearch("");
      }
      return !prev;
    });
  }, [disabled, countries, selectedCountry]);

  const selectItem = useCallback(
    (country) => {
      onSelectCountry(country);
      setIsOpen(false);
      setSearch("");
    },
    [onSelectCountry],
  );

  const handleTriggerKeyDown = useCallback(
    (e) => {
      if (!isOpen) {
        if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
          e.preventDefault();
          toggleOpen();
        }
        return;
      }
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : 0,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightIndex((prev) =>
            prev > 0 ? prev - 1 : filtered.length - 1,
          );
          break;
        case "Enter":
          e.preventDefault();
          if (highlightIndex >= 0 && highlightIndex < filtered.length)
            selectItem(filtered[highlightIndex]);
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setSearch("");
          break;
        default:
          break;
      }
    },
    [isOpen, filtered, highlightIndex, toggleOpen, selectItem],
  );

  const handleSearchKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : 0,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightIndex((prev) =>
            prev > 0 ? prev - 1 : filtered.length - 1,
          );
          break;
        case "Enter":
          e.preventDefault();
          if (highlightIndex >= 0 && highlightIndex < filtered.length)
            selectItem(filtered[highlightIndex]);
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setSearch("");
          break;
        default:
          break;
      }
    },
    [filtered, highlightIndex, selectItem],
  );

  useEffect(() => {
    setHighlightIndex(filtered.length > 0 ? 0 : -1);
  }, [search, filtered.length]);

  const selectedFlag = selectedCountry ? getFlag(selectedCountry) : "";
  const selectedDialCode = selectedCountry ? getDialCode(selectedCountry) : "";
  const selectedName = selectedCountry?.name || "";

  return (
    <div className="space-y-1">
      <span className="overline block mb-2">Phone number *</span>

      <div
        className={`flex border rounded-lg overflow-visible transition-colors ${
          error
            ? "border-red-400"
            : "border-white/[0.1] focus-within:border-[#C6A87C]"
        }`}
      >
        {/* Country code trigger */}
        <div ref={containerRef} className="relative shrink-0">
          <button
            type="button"
            onClick={toggleOpen}
            onKeyDown={handleTriggerKeyDown}
            disabled={disabled}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-label={`Country code: ${selectedName || selectedDialCode || "Select"}`}
            data-testid="register-country-code-select"
            className={`
              flex items-center gap-2 px-3 py-3
              bg-white/[0.06] border-r border-white/[0.1]
              min-w-[110px] h-full transition-colors
              focus:outline-none
              disabled:opacity-60 disabled:cursor-not-allowed
              ${isOpen ? "border-r-[#C6A87C]" : "hover:bg-white/[0.08]"}
            `}
          >
            {selectedFlag ? (
              <span className="text-lg leading-none shrink-0">
                {selectedFlag}
              </span>
            ) : (
              <span className="text-white/30 text-sm">Code</span>
            )}
            {selectedDialCode && (
              <span className="text-white/80 text-sm font-medium whitespace-nowrap">
                {selectedDialCode}
              </span>
            )}
            <ChevronDown
              size={14}
              className={`ml-auto text-white/30 shrink-0 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Dropdown */}
          {isOpen && (
            <div
              ref={listRef}
              role="listbox"
              aria-label="Select country code"
              data-testid="register-country-code-dropdown"
              className="
                absolute left-0 top-[calc(100%+4px)] z-50
                w-[280px] rounded-xl
                border border-white/[0.1]
                bg-[#0A1D1A]/95 backdrop-blur-xl
                shadow-2xl overflow-hidden
              "
            >
              <div className="p-2 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 bg-white/[0.04] rounded-lg px-3 py-2">
                  <Search size={14} className="text-white/30 shrink-0" />
                  <input
                    ref={searchRef}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search country or code…"
                    className="bg-transparent text-sm text-white/80 placeholder:text-white/30 outline-none flex-1"
                    aria-label="Search countries"
                    data-testid="register-country-search"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      aria-label="Clear search"
                    >
                      <X
                        size={12}
                        className="text-white/30 hover:text-white/60 transition-colors"
                      />
                    </button>
                  )}
                </div>
              </div>

              <ul
                className="max-h-[220px] overflow-y-auto py-1"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(198,168,124,0.3) transparent",
                }}
              >
                {filtered.length === 0 ? (
                  <li className="px-3 py-4 text-sm text-white/40 text-center">
                    No countries found
                  </li>
                ) : (
                  filtered.map((c, idx) => {
                    const dc = getDialCode(c);
                    if (!dc) return null;
                    const flag = getFlag(c);
                    const name = c.name || getIso2(c) || dc;
                    const iso = getIso2(c);
                    const isSelected =
                      selectedCountry && getIso2(selectedCountry) === iso;
                    const isHighlighted = idx === highlightIndex;

                    return (
                      <li
                        key={c._id || c.id || `${dc}-${idx}`}
                        role="option"
                        aria-selected={!!isSelected}
                        data-testid={`register-country-option-${iso || idx}`}
                        onClick={() => selectItem(c)}
                        onMouseEnter={() => setHighlightIndex(idx)}
                        className={`
                          flex items-center gap-3 px-3 py-2.5
                          cursor-pointer transition-colors
                          ${isHighlighted ? "bg-white/[0.08]" : "hover:bg-white/[0.04]"}
                          ${isSelected ? "text-[#C6A87C]" : "text-white/85"}
                        `}
                      >
                        <span className="text-lg leading-none shrink-0 w-6 text-center">
                          {flag}
                        </span>
                        <span className="text-sm truncate flex-1">{name}</span>
                        <span
                          className={`text-xs font-mono shrink-0 ${
                            isSelected ? "text-[#C6A87C]" : "text-white/50"
                          }`}
                        >
                          {dc}
                        </span>
                        {isSelected && (
                          <Check
                            size={14}
                            className="text-[#C6A87C] shrink-0"
                          />
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Phone input */}
        <input
          type="tel"
          value={phone}
          onChange={(e) => onPhoneChange(e.target.value)}
          placeholder="Enter phone number"
          disabled={disabled}
          data-testid="register-phone-input"
          className="flex-1 bg-transparent px-4 py-3 text-white/90 placeholder:text-white/30 text-sm outline-none disabled:opacity-60"
        />
      </div>

      {error && <p className="text-red-300 text-xs mt-1">{error}</p>}
    </div>
  );
});

// ---------------------------------------------------------------------------
// STEP 1 — PROFILE
// ---------------------------------------------------------------------------

const StepProfile = ({
  form,
  setForm,
  onNext,
  err,
  countries,
  countriesLoading,
  selectedCountry,
  setSelectedCountry,
  phoneVerified,
}) => {
  const [localErr, setLocalErr] = useState({});

  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
    return regex.test(email.trim());
  };

  const handleNext = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = "First name is required";
    if (!form.lastName.trim()) errs.lastName = "Last name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!validateEmail(form.email)) errs.email = "Enter a valid email";
    if (!phoneVerified) {
      if (!selectedCountry) errs.country = "Please select your country code";
      if (!form.phoneNumber.trim())
        errs.phoneNumber = "Phone number is required";
    }

    setLocalErr(errs);
    if (Object.keys(errs).length === 0) onNext();
  };

  const field = (key) => ({
    value: form[key],
    onChange: (e) => {
      setForm({ ...form, [key]: e.target.value });
      if (localErr[key]) setLocalErr((p) => ({ ...p, [key]: "" }));
    },
  });

  return (
    <div className="space-y-5">
      <div>
        <p className="overline mb-1">Step 1 of 4</p>
        <h2 className="font-display text-3xl tracking-tight">
          Create your profile
        </h2>
        <p className="text-white/50 text-sm mt-1">Tell us who you are</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* First Name */}
        <label className="block">
          <span className="overline block mb-2">First name *</span>
          <input
            required
            {...field("firstName")}
            data-testid="register-firstname-input"
            placeholder="John"
            className={`w-full bg-white/[0.04] border rounded-lg px-4 py-3
                        focus:border-[#C6A87C] focus:outline-none transition-colors ${
                          localErr.firstName
                            ? "border-red-400"
                            : "border-white/[0.1]"
                        }`}
          />
          {localErr.firstName && (
            <p className="text-red-300 text-xs mt-1">{localErr.firstName}</p>
          )}
        </label>

        {/* Last Name */}
        <label className="block">
          <span className="overline block mb-2">Last name *</span>
          <input
            required
            {...field("lastName")}
            data-testid="register-lastname-input"
            placeholder="Doe"
            className={`w-full bg-white/[0.04] border rounded-lg px-4 py-3
                        focus:border-[#C6A87C] focus:outline-none transition-colors ${
                          localErr.lastName
                            ? "border-red-400"
                            : "border-white/[0.1]"
                        }`}
          />
          {localErr.lastName && (
            <p className="text-red-300 text-xs mt-1">{localErr.lastName}</p>
          )}
        </label>

        {/* Email */}
        <label className="block md:col-span-2">
          <span className="overline block mb-2">Email *</span>
          <input
            required
            type="email"
            {...field("email")}
            data-testid="register-email-input"
            placeholder="john@example.com"
            className={`w-full bg-white/[0.04] border rounded-lg px-4 py-3
                        focus:border-[#C6A87C] focus:outline-none transition-colors ${
                          localErr.email
                            ? "border-red-400"
                            : "border-white/[0.1]"
                        }`}
          />
          {localErr.email && (
            <p className="text-red-300 text-xs mt-1">{localErr.email}</p>
          )}
        </label>

        {/* Country + Phone */}
        <div className="md:col-span-2">
          {phoneVerified ? (
            // ── Read-only verified phone display ──
            <div className="space-y-1">
              <span className="overline block mb-2">Phone number</span>
              <div
                className="flex items-center border border-[#C6A87C]/30 
                            rounded-lg overflow-hidden bg-[#C6A87C]/5"
              >
                {/* Country code — locked */}
                <div
                  className="flex items-center gap-2 px-3 py-3 
                              bg-white/[0.04] border-r border-white/[0.1]
                              min-w-[110px]"
                >
                  {selectedCountry && (
                    <span className="text-lg leading-none shrink-0">
                      {getFlag(selectedCountry)}
                    </span>
                  )}
                  <span className="text-white/80 text-sm font-medium">
                    {selectedCountry ? getDialCode(selectedCountry) : ""}
                  </span>
                </div>

                {/* Phone number — locked */}
                <span className="flex-1 px-4 py-3 text-white/80 text-sm">
                  {form.phoneNumber}
                </span>

                {/* Verified badge */}
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 mr-2 
                              rounded-full bg-[#C6A87C]/15"
                >
                  <CheckCircle2 size={13} className="text-[#C6A87C]" />
                  <span className="text-[#C6A87C] text-xs font-medium">
                    Verified
                  </span>
                </div>
              </div>
            </div>
          ) : countriesLoading ? (
            <div className="flex items-center gap-2 py-3">
              <div className="w-4 h-4 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
              <span className="text-white/40 text-sm">Loading countries…</span>
            </div>
          ) : (
            <CountryPhonePicker
              countries={countries}
              selectedCountry={selectedCountry}
              onSelectCountry={(c) => {
                setSelectedCountry(c);
                if (localErr.country)
                  setLocalErr((p) => ({ ...p, country: "" }));
              }}
              phone={form.phoneNumber}
              onPhoneChange={(val) => {
                setForm({ ...form, phoneNumber: val });
                if (localErr.phoneNumber)
                  setLocalErr((p) => ({ ...p, phoneNumber: "" }));
              }}
              error={localErr.country || localErr.phoneNumber}
            />
          )}
        </div>
      </div>

      {err && (
        <p className="text-sm text-red-300" data-testid="register-error">
          {err}
        </p>
      )}

      <button
        type="button"
        onClick={handleNext}
        className="btn-gold w-full justify-center"
      >
        Continue <ArrowRight size={16} />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// STEP 2 — OTP VERIFICATION
// ---------------------------------------------------------------------------

const StepOtp = ({ phoneNumber, onVerified, onBack }) => {
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [err, setErr] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [testOtp, setTestOtp] = useState(null); // show on screen like Login
  const inputRefs = useRef([]);

  // Send OTP on mount
  useEffect(() => {
    sendOtp();
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const sendOtp = async () => {
    setSending(true);
    setErr("");
    setTestOtp(null);
    try {
      // Same as Login — /login sends OTP
      const res = await sendOtpApi({ phoneNumber });
      console.log("Send OTP response:", res);

      if (res?.status === true || res?.status === 200) {
        setOtpSent(true);
        setCountdown(60);
        toast.success("OTP sent to your phone");

        // Show test OTP on screen — same as Login does
        const otpFromApi = res?.data?.otp ?? res?.data?.data?.otp ?? null;
        setTestOtp(otpFromApi);
      } else {
        setErr(res?.message || "Failed to send OTP");
      }
    } catch (e) {
      setErr(
        e?.response?.data?.message || e?.message || "Failed to send OTP"
      );
    } finally {
      setSending(false);
    }
  };

  // OTP digits — 4 digits like Login
  const digits = useMemo(() => {
    const v = String(otp || "");
    return Array.from({ length: 4 }, (_, i) => v[i] || "");
  }, [otp]);

  const focus = useCallback(
    (i) => inputRefs.current[i]?.focus(),
    []
  );

  const handleOtpChange = (index, e) => {
    const raw = e.target.value || "";
    const onlyDigits = raw.replace(/\D/g, "");

    // Handle paste into single box
    if (onlyDigits.length > 1) {
      const merged = (otp + onlyDigits).replace(/\D/g, "").slice(0, 4);
      setOtp(merged);
      focus(Math.min(merged.length, 3));
      setErr("");
      return;
    }

    const d = onlyDigits.slice(-1);
    const next = digits.slice();
    next[index] = d;
    setOtp(next.join(""));
    setErr("");

    if (d && index < 3) {
      focus(index + 1);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const next = digits.slice();
        next[index] = "";
        setOtp(next.join(""));
      } else if (index > 0) {
        focus(index - 1);
        const next = digits.slice();
        next[index - 1] = "";
        setOtp(next.join(""));
      }
    }
    if (e.key === "ArrowLeft" && index > 0) focus(index - 1);
    if (e.key === "ArrowRight" && index < 3) focus(index + 1);
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();
    setOtp(text.slice(0, 4));
    focus(Math.min(text.length, 3));
  };

  const handleVerify = async () => {
    if (otp.length < 4) {
      setErr("Please enter the complete 4-digit OTP");
      return;
    }

    setVerifying(true);
    setErr("");
    try {
      // Same as Login confirmOtp
      const res = await verifyOtpApi({ phoneNumber, otp });
      console.log("Verify OTP response:", res);

      if (res?.status === true) {
        toast.success("Phone verified successfully");
        if (res?.data?.token) {
          localStorage.setItem("authToken", res.data.token);
        }
        onVerified();
      } else {
        setErr(res?.message || "Invalid OTP. Please try again.");
        setOtp("");
        focus(0);
      }
    } catch (e) {
      setErr(
        e?.response?.data?.message ||
          e?.message ||
          "OTP verification failed"
      );
      setOtp("");
      focus(0);
    } finally {
      setVerifying(false);
    }
  };

  const otpComplete = otp.length === 4;

  return (
    <div className="space-y-6">
      <div>
        <p className="overline mb-1">Step 2 of 4</p>
        <h2 className="font-display text-3xl tracking-tight">
          Verify your number
        </h2>
        <p className="text-white/50 text-sm mt-1">
          We sent a 4-digit code to
        </p>
        <p className="text-[#C6A87C] font-medium mt-1">{phoneNumber}</p>
      </div>

      {/* Icon */}
      <div className="flex justify-center">
        <div className="w-16 h-16 rounded-full bg-[#C6A87C]/10 border border-[#C6A87C]/20 flex items-center justify-center">
          {sending ? (
            <div className="w-6 h-6 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
          ) : (
            <ShieldCheck size={28} className="text-[#C6A87C]" />
          )}
        </div>
      </div>

      {sending && (
        <p className="text-white/40 text-sm text-center">Sending OTP…</p>
      )}

      {/* OTP Boxes — 4 digits like Login */}
      {otpSent && !sending && (
        <>
          <div className="flex gap-3 justify-center" onPaste={handlePaste}>
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(i, e)}
                onKeyDown={(e) => handleOtpKeyDown(i, e)}
                data-testid={`otp-input-${i}`}
                className={`
                  text-center text-xl font-bold
                  bg-white/[0.04] border rounded-xl
                  text-white caret-[#C6A87C]
                  focus:outline-none focus:border-[#C6A87C]
                  transition-colors
                  ${digit ? "border-[#C6A87C]/60" : "border-white/[0.1]"}
                  ${err ? "border-red-400" : ""}
                `}
                style={{ width: "52px", height: "56px" }}
              />
            ))}
          </div>

          {/* ── Test OTP shown on screen — same as Login ── */}
          {testOtp != null && (
            <p className="text-sm text-white/65 text-center">
              Test OTP:{" "}
              <span className="text-[#C6A87C] font-semibold">{testOtp}</span>
            </p>
          )}

          {err && (
            <p className="text-red-300 text-xs text-center">{err}</p>
          )}

          {/* Resend */}
          <div className="text-center">
            {countdown > 0 ? (
              <p className="text-white/35 text-sm">
                Resend OTP in{" "}
                <span className="text-[#C6A87C]">{countdown}s</span>
              </p>
            ) : (
              <button
                type="button"
                onClick={sendOtp}
                className="text-[#C6A87C] text-sm hover:underline transition-colors"
              >
                Resend OTP
              </button>
            )}
          </div>
        </>
      )}

      {/* Buttons */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={verifying || sending}
          className="flex items-center gap-2 px-5 py-3 rounded-xl border border-white/[0.1] text-white/60 hover:border-white/30 transition-colors text-sm disabled:opacity-40"
        >
          <ArrowLeft size={15} /> Back
        </button>
        <button
          type="button"
          onClick={handleVerify}
          disabled={!otpComplete || verifying || sending}
          data-testid="otp-verify-btn"
          className="btn-gold flex-1 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {verifying ? "Verifying…" : "Verify"}{" "}
          {!verifying && <ArrowRight size={16} />}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// STEP 3 — CHAPTER SELECTION
// ---------------------------------------------------------------------------

const StepChapter = ({
  selectedChapter,
  onSelect,
  onNext,
  onBack,
  selectedCountry,
}) => {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchErr, setFetchErr] = useState("");

  useEffect(() => {
    if (!selectedCountry?.isoCode) return;
    fetchChaptersData(selectedCountry.isoCode);
  }, [selectedCountry]);

  const fetchChaptersData = async (isoCode) => {
    setLoading(true);
    setFetchErr("");
    setChapters([]);
    try {
      const res = await getChapters(isoCode);
      if (
        res?.status === 200 &&
        Array.isArray(res.data) &&
        res.data.length > 0
      ) {
        setChapters(res.data.map((name, i) => ({ id: i + 1, name })));
      } else {
        setFetchErr("No centres found for your country.");
      }
    } catch {
      setFetchErr("Could not load centres. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="overline mb-1">Step 3 of 4</p>
        <h2 className="font-display text-3xl tracking-tight">
          Select Your Centre
        </h2>
        <p className="text-white/50 text-sm mt-1">
          Choose the centre closest to you
        </p>
      </div>

      {selectedCountry && (
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#C6A87C]/10 border border-[#C6A87C]/20 w-fit">
          <span className="text-lg">{getFlag(selectedCountry)}</span>
          <span className="text-[#C6A87C] text-sm font-medium">
            {selectedCountry.name}
          </span>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-3 py-6 justify-center">
          <div className="w-5 h-5 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
          <span className="text-white/50 text-sm">
            Finding centres near you…
          </span>
        </div>
      )}

      {!loading && fetchErr && (
        <div className="text-center py-4">
          <p className="text-red-300 text-sm">{fetchErr}</p>
          <button
            type="button"
            onClick={() => fetchChaptersData(selectedCountry?.isoCode)}
            className="mt-3 text-[#C6A87C] text-xs hover:underline"
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !fetchErr && chapters.length === 0 && selectedCountry && (
        <p className="text-white/40 text-sm text-center py-4">
          No centres available for your region.
        </p>
      )}

      {!loading && chapters.length > 0 && (
        <>
          <p className="text-white/40 text-xs">
            {chapters.length} {chapters.length === 1 ? "centre" : "centres"}{" "}
            available
          </p>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {chapters.map((ch) => {
              const isSelected = selectedChapter?.id === ch.id;
              return (
                <button
                  key={ch.id}
                  type="button"
                  onClick={() => onSelect(ch)}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-colors flex items-center gap-3 ${
                    isSelected
                      ? "bg-[#C6A87C]/15 border-[#C6A87C]"
                      : "bg-white/[0.03] border-white/[0.08] hover:border-white/20"
                  }`}
                >
                  <MapPin
                    size={16}
                    className={isSelected ? "text-[#C6A87C]" : "text-white/30"}
                  />
                  <span
                    className={`text-sm flex-1 ${
                      isSelected ? "text-white font-medium" : "text-white/70"
                    }`}
                  >
                    {ch.name}
                  </span>
                  {isSelected && (
                    <CheckCircle2 size={16} className="text-[#C6A87C]" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-3 rounded-xl border border-white/[0.1] text-white/60 hover:border-white/30 transition-colors text-sm"
        >
          <ArrowLeft size={15} /> Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!selectedChapter}
          className="btn-gold flex-1 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Continue <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// STEP 4 — INTERESTS
// ---------------------------------------------------------------------------

const StepInterests = ({ picked, setPicked, onBack, onSubmit, busy, err }) => {
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchInterestsData();
  }, []);

  const fetchInterestsData = async () => {
    setLoading(true);
    try {
      const res = await getInterests();
      if (
        res?.status === true &&
        Array.isArray(res.data) &&
        res.data.length > 0
      ) {
        setInterests(res.data);
      } else {
        setInterests(STATIC_INTERESTS);
      }
    } catch {
      setInterests(STATIC_INTERESTS);
    } finally {
      setLoading(false);
    }
  };

  const toggle = (name) => {
    setPicked((p) => {
      if (p.includes(name)) return p.filter((x) => x !== name);
      if (p.length >= 8) {
        toast.warning("You can select up to 8 interests");
        return p;
      }
      return [...p, name];
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="overline mb-1">Step 4 of 4</p>
        <h2 className="font-display text-3xl tracking-tight">Your Interests</h2>
        <p className="text-white/50 text-sm mt-1">
          Select at least 1 topic you're passionate about —{" "}
          <span className="text-[#C6A87C]">{picked.length} / 8 selected</span>
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-3 py-8">
          <div className="w-5 h-5 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
          <span className="text-white/50 text-sm">Loading interests…</span>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="register-interests">
          {interests.map((item) => (
            <button
              key={item._id}
              type="button"
              onClick={() => toggle(item.name)}
              data-testid={`register-interest-${item.name}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                picked.includes(item.name)
                  ? "bg-[#C6A87C] text-[#061412] border-[#C6A87C]"
                  : "border-white/[0.15] text-white/70 hover:border-white/30"
              }`}
            >
              {item.name}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-start gap-2 bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
        <Lightbulb size={16} className="text-[#C6A87C] mt-0.5 flex-shrink-0" />
        <p className="text-white/40 text-xs leading-relaxed">
          We'll personalise your experience based on your interests. You can
          change these anytime in settings.
        </p>
      </div>

      {err && (
        <p className="text-sm text-red-300" data-testid="register-error">
          {err}
        </p>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-3 rounded-xl border border-white/[0.1] text-white/60 hover:border-white/30 transition-colors text-sm"
        >
          <ArrowLeft size={15} /> Back
        </button>
        <button
          type="button"
          onClick={() => onSubmit(false)}
          disabled={busy || picked.length < 1}
          data-testid="register-submit"
          className="btn-gold flex-1 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? "Creating…" : "Join vmeetio"} <ArrowRight size={16} />
        </button>
      </div>

      <button
        type="button"
        onClick={() => onSubmit(true)}
        disabled={busy}
        className="w-full text-center text-sm text-white/35 hover:text-white/55 transition-colors py-1"
      >
        Skip for now
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// MAIN — REGISTER PAGE
// ---------------------------------------------------------------------------

export default function Register() {
  const nav = useNavigate();
  const location = useLocation();

  const preVerified = location.state?.phoneVerified === true;
  const prePhone = location.state?.phoneNumber || "";

  // 0=profile, 1=otp, 2=chapter, 3=interests
  const [step, setStep] = useState(0);
  const [phoneVerified, setPhoneVerified] = useState(preVerified);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "", // will be set after countries load
  });
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Full phone number with dial code — built when step 1 is complete
  const fullPhoneNumber = useMemo(() => {
    // If pre-verified, use the original full phone number directly
    if (preVerified && prePhone) return prePhone;

    const dial = getDialCode(selectedCountry);
    const num = form.phoneNumber.trim();
    if (!dial || !num) return num;
    if (num.startsWith(dial)) return num;
    return `${dial}${num}`;
  }, [selectedCountry, form.phoneNumber, preVerified, prePhone]);

    useEffect(() => {
      fetchCountries();
    }, []);

    const fetchCountries = async () => {
      setCountriesLoading(true);
      try {
        const res = await getCountries();
        console.log("Countries API:", res);

        if (res?.success && Array.isArray(res?.data)) {
          setCountries(res.data);

          // Auto-populate country + phone if pre-verified from Login
          if (preVerified && prePhone) {
            autoPopulatePhone(res.data, prePhone);
          }
        }
      } catch (e) {
        console.error("Failed to fetch countries:", e);
        toast.error("Could not load countries");
      } finally {
        setCountriesLoading(false);
      }
    };

    // Match the full phone number against country dial codes
    // to auto-select country and extract local number
    const autoPopulatePhone = (countriesList, fullPhone) => {
      if (!fullPhone || !countriesList.length) return;

      // Sort by dial code length descending so we match longest first
      // e.g. +592 matches before +5
      const sorted = [...countriesList].sort(
        (a, b) => getDialCode(b).length - getDialCode(a).length,
      );

      let matchedCountry = null;
      let localNumber = fullPhone;

      for (const c of sorted) {
        const dc = getDialCode(c);
        if (dc && fullPhone.startsWith(dc)) {
          matchedCountry = c;
          localNumber = fullPhone.slice(dc.length);
          break;
        }
      }

      if (matchedCountry) {
        setSelectedCountry(matchedCountry);
      }

      setForm((prev) => ({
        ...prev,
        phoneNumber: localNumber,
      }));
    };

  useEffect(() => {
    setSelectedChapter(null);
  }, [selectedCountry]);

  const handleSubmit = async (skip = false) => {
    setBusy(true);
    setErr("");

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phoneNumber: fullPhoneNumber,
      chapterName: selectedChapter?.name || "",
      isoCode: selectedCountry?.isoCode || "",
      interests: skip ? [] : picked,
    };

    console.log("Register payload:", payload);

    try {
      const res = await registerApi(payload);
      console.log("Registration response:", res);

      if (res?.status === true) {
        if (res?.data?.token) {
          localStorage.setItem("authToken", res.data.token);
        }
        toast.success("Welcome to vmeetio.");
        nav("/login");
      } else {
        setErr(res?.message || "Registration failed. Please try again.");
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Something went wrong";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-[80vh]">
      <div className="container-x grid md:grid-cols-12 gap-12 py-16 md:py-24">
        {/* Left panel */}
        <div className="md:col-span-5 hidden md:block">
          <p className="overline mb-5">Create your profile</p>
          <h1 className="font-display font-light text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6">
            Tell us where you sit,
            <br />
            <span className="italic text-[#C6A87C]">
              and what you're after.
            </span>
          </h1>
          <p className="text-white/65 max-w-md leading-relaxed">
            Anyone can register. We use your profile to match you to the right
            table — and the right people.
          </p>
          <p className="text-white/55 max-w-md mt-6 text-sm">
            <Link
              to="/network#apply-membership"
              className="text-[#C6A87C] hover:underline"
            >
              Become a VIP
            </Link>{" "}
            for reserved seats at VIP tables and curated intros.
          </p>

          {/* Step summary */}
          <div className="mt-12 space-y-3">
            {[
              {
                label: "Profile",
                done: step > 0,
                active: step === 0,
                value: form.firstName
                  ? `${form.firstName} ${form.lastName}`.trim()
                  : null,
              },
              {
                label: "Verify",
                done: phoneVerified || step > 1,
                active: step === 1,
                value: phoneVerified ? "Phone verified ✓" : null,
              },
              {
                label: "Centre",
                done: step > 2,
                active: step === 2,
                value: selectedChapter?.name || null,
              },
              {
                label: "Interests",
                done: false,
                active: step === 3,
                value: picked.length > 0 ? `${picked.length} selected` : null,
              },
            ].map((s, i) => (
              <div
                key={i}
                className={`flex items-center gap-3 text-sm transition-opacity ${
                  s.active ? "opacity-100" : "opacity-40"
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    s.done
                      ? "bg-[#C6A87C]"
                      : s.active
                        ? "bg-[#C6A87C]/60"
                        : "bg-white/20"
                  }`}
                />
                <span className="text-white/70">{s.label}</span>
                {s.value && (
                  <span className="text-[#C6A87C] text-xs truncate max-w-[140px]">
                    — {s.value}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right panel */}
        <div className="md:col-span-6 md:col-start-7">
          <div className="glass rounded-2xl p-8">
            <StepIndicator current={step} />

            {step === 0 && (
              <StepProfile
                form={form}
                setForm={setForm}
                onNext={() => {
                  setErr("");
                  if (phoneVerified) {
                    setStep(2);
                  } else {
                    setStep(1);
                  }
                }}
                err={err}
                countries={countries}
                countriesLoading={countriesLoading}
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                phoneVerified={phoneVerified}
              />
            )}

            {step === 1 && (
              <StepOtp
                phoneNumber={fullPhoneNumber}
                onVerified={() => {
                  setPhoneVerified(true);
                  setStep(2);
                }}
                onBack={() => setStep(0)}
              />
            )}

            {step === 2 && (
              <StepChapter
                selectedChapter={selectedChapter}
                onSelect={setSelectedChapter}
                onNext={() => setStep(3)}
                onBack={() => {
                  // Skip back to profile if OTP already verified
                  if (phoneVerified) {
                    setStep(0);
                  } else {
                    setStep(1);
                  }
                }}
                selectedCountry={selectedCountry}
              />
            )}

            {step === 3 && (
              <StepInterests
                picked={picked}
                setPicked={setPicked}
                onBack={() => setStep(2)}
                onSubmit={handleSubmit}
                busy={busy}
                err={err}
              />
            )}

            <p className="text-sm text-white/55 text-center mt-5">
              Already a member?{" "}
              <Link
                to="/login"
                className="text-[#C6A87C] hover:underline"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
